import { Typography, Box, Tab, Tabs } from '@mui/material';
import React, { useState, useCallback, useEffect } from 'react';
import { GamifyIcon } from 'application/components/GamifyIcon';
import PinsConfig from './PinsConfig.view';
import { useCategories } from 'application/hooks/useCategories';
import * as types from 'application/_types/types.type';
import StageConfig from './StageConfig.view';
import FormConfig from './FormConfig.view';
import FeaturesConfig from './FeaturesConfig.view';
import MessagesConfig from './MessagesConfig.view';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const tabSx = (theme: any) => ({
  display: 'flex',
  height: '44px',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  '&.Mui-selected': {
    color: theme.custom?.text,
  },
});

const CustomTabPanel = ({
  children,
  value,
  index,
  ...other
}: TabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
  </div>
);

export default function OrgConfig() {
  const { categories } = useCategories();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedCRM, setSelectedCRM] =
    useState<types.StageCategoryPartial | null>(null);

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setSelectedTab(newValue);
    },
    []
  );

  useEffect(() => {
    // console.log('categories', categories);
    if (categories.length > 0) {
      const defaultCategory: types.StageCategoryPartial | undefined =
        categories.find((category) => category.isDefault);
      if (defaultCategory) {
        setSelectedCRM(defaultCategory as types.StageCategoryPartial);
      } else {
        setSelectedCRM(categories[0] as types.StageCategoryPartial);
      }
    }
  }, [categories]);
  return (
    <Box style={{ minHeight: '80vh' }}>
      <Box
        sx={(theme) => ({
          backgroundColor: theme.custom?.background1,
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${theme.custom?.stroke}`,
          padding: 2,
        })}
      >
        <Typography
          sx={(theme) => ({
            fontWeight: 780,
            fontSize: 20,
            color: theme.custom?.text,
          })}
        >
          Sales Tools Customization
        </Typography>
        <Typography
          sx={(theme) => ({
            fontWeight: 400,
            fontSize: 15,
            color: theme.custom?.textLight,
          })}
        >
          {`Customize pins, deals, and forms to align with your unique sales process.`}
        </Typography>

        <Box
          sx={(theme) => ({
            display: 'flex',
            gap: 2,
            borderBottom: `1px solid ${theme.custom?.stroke}`,
          })}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            aria-label="nav"
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              '& .MuiTabs-indicator': {
                backgroundColor: theme.custom?.text,
                height: 2,
              },
              '& .MuiTab-root': {
                color: theme.custom?.textLight,
              },
              '& .MuiTab-root.Mui-selected': {
                color: theme.custom?.text,
              },
            })}
          >
            <Tab
              sx={tabSx}
              value={0}
              className="custom-tab"
              icon={<GamifyIcon icon="pin" color="inherit" />}
              label="Pins"
            />
            <Tab
              value={1}
              sx={tabSx}
              icon={<GamifyIcon icon="stack" color="inherit" />}
              className="custom-tab"
              label="Stages"
            />
            <Tab
              value={2}
              sx={tabSx}
              icon={<GamifyIcon icon="notes" color="inherit" />}
              className="custom-tab"
              label="Form"
            />
            <Tab
              value={3}
              sx={tabSx}
              icon={<GamifyIcon icon="gear" color="inherit" />}
              className="custom-tab"
              label="Features Config"
            />

            <Tab
              value={4}
              sx={tabSx}
              icon={<GamifyIcon icon="gear" color="inherit" />}
              className="custom-tab"
              label="Preset messages"
            />
          </Tabs>
        </Box>
        <Box>
          <CustomTabPanel value={selectedTab} index={0}>
            <PinsConfig selectedCRM={selectedCRM} />
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={1}>
            <StageConfig selectedCRM={selectedCRM} />
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={2}>
            <FormConfig selectedCRM={selectedCRM} />
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={3}>
            <FeaturesConfig selectedCRM={selectedCRM} categories={categories} />
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={4}>
            <MessagesConfig />
          </CustomTabPanel>
        </Box>
      </Box>
    </Box>
  );
}
